import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "../lib/api";

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Ava — Shumway Tech's AI concierge. What are you looking to build?" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit a snag. Try again in a moment, or use the contact form below." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open-button"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#1A1A1A] text-white pl-4 pr-5 py-3 shadow-[0_10px_40px_rgba(0,0,0,0.18)] hover:bg-[#C5A059] transition-colors"
        >
          <span className="relative inline-block w-2 h-2 rounded-full bg-[#C5A059] pulse-dot" />
          <MessageCircle size={18}/>
          <span className="text-[14px] tracking-wide">Chat with Ava</span>
        </button>
      )}

      {open && (
        <div data-testid="chat-window" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[380px] h-[540px] bg-white border border-[#EBE8DF] rounded-xl overflow-hidden flex flex-col shadow-[0_18px_60px_rgba(0,0,0,0.12)]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#EBE8DF] bg-[#FDFBF7]">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_custom-crm-sites/artifacts/g2kbfara_D85C58C0-0C17-4493-BAD4-0449B78C99FB.png"
                alt="Shumway Tech"
                style={{ width: 36, height: 36, objectFit: "contain" }}
              />
              <div>
                <p className="font-display text-[18px] leading-none">Ava</p>
                <p className="text-[11px] text-[#8B8C89] mt-1">Shumway Tech AI concierge</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close-button" className="text-[#5C5C5C] hover:text-[#1A1A1A]">
              <X size={18}/>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FDFBF7]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] text-[14px] leading-relaxed px-3.5 py-2.5 ${m.role === "user" ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#EBE8DF] text-[#1A1A1A]"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#EBE8DF] px-3.5 py-2.5 text-[14px] text-[#8B8C89]">Ava is typing…</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[#EBE8DF] bg-white p-3 flex items-end gap-2"
          >
            <textarea
              data-testid="chat-input"
              rows={1} value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }}}
              placeholder="Ask about pricing, timelines, AI SEO…"
              className="flex-1 resize-none px-3 py-2 text-[14px] border border-[#EBE8DF] focus:border-[#C5A059] outline-none rounded"
            />
            <button type="submit" data-testid="chat-send-button" disabled={sending} className="btn-gold p-3" aria-label="Send">
              <Send size={16}/>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
